import React, { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Tooltip } from "@mui/material";
import ElipsisCell from "../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../components/ElipsisText";
import Badge from "../../../../components/Badge";
import { RowClassName, PermissionModulesEnum, PermissionActionsEnum } from "../../../../enums";
import { TransactionStatus } from "../../../../interfaces/transaction.interface";
import { Edit as EditInterface } from "../../../../interfaces/invoice.interface";
import { TableContext } from "../../../../contexts/TableContext";
import { AuthContext } from "../../../../contexts/AuthContext";
import { VALIDATION_FIELD_MAPPING, RESERVATION_STATUS } from "../../../../utils/appConstant";
import Checkbox from "../../../../components/Checkbox";
import { hasResourseAccess, formatDollarAmount } from "../../../../utils/methods";

const TEXT_CLASS_VARIANT: Record<string, string> = {
  Matched: 'text-success',
  Unmatched: 'text-danger',
  Pending: 'text-warning'
};

interface RowProps {
  item: any;
  className?: RowClassName;
  formData: EditInterface;
  view: boolean;
  onInputChange: (e: any, name: string, type: string, id?: string) => void;
  onOpen: (paramId: string, mode: "view" | "edit") => void;
  onPayoutAmtClick?: (item: any) => void;
}

const Row: React.FC<RowProps> = ({ item, className, formData, view, onInputChange, onOpen, onPayoutAmtClick }) => {
  const { t } = useTranslation();
  const { headCells } = useContext(TableContext);
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Reservation]: [PermissionActionsEnum.Update] }] });

  const handleCheckInDetailsClick = useCallback((id: string) => {
    onOpen(id, (hasEditAccess && !view) ? 'edit' : 'view');
  }, [view])

  const getStatusVariant = (status: TransactionStatus) => {
    if (status === VALIDATION_FIELD_MAPPING.unBilled) return "secondary";
    else if (status === VALIDATION_FIELD_MAPPING.invoice) return "warning"
    else return "info"
  }

  const checkInDetailsNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 34 }}>
        <img width={32} height={32} src={`/assets/images/${item?.platform?.platform_name}.svg`} />
      </div>
      <div style={{ width: 'calc(100% - 44px)' }}>
        <ElipsisText
          width="100%"
          text={`${item?.check_in_date_list ?? '-'} - ${item?.check_out_date_list ?? '-'}`}
          className="h6 mb-1 text-decoration-underline hyperlink"
          onClick={() => handleCheckInDetailsClick(item.id)}
        />
        <ElipsisText width="100%" text={t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.totalNights", { totalNights: item?.total_nights ?? "-" })} />
      </div>
    </div>
  )

  const guestNode = (
    <div>
      <ElipsisText width="100%" text={item.guest_name || '-'} className="h6 mb-1" />
      <ElipsisText width="100%" text={t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.totalGuests", { totalGuests: item.total_guests || "-" })} />
    </div>
  )

  const isVerified = item.verification_status === 'verified';

  const hostPayoutNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <div
        className={clsx(TEXT_CLASS_VARIANT[item.reconciliation_status], "cursor-pointer")}
        style={{ borderBottom: '2px dotted', display: 'inline-block' }}
        onClick={() => onPayoutAmtClick && onPayoutAmtClick(item)}
      >
        {item.formatted_revenue ? formatDollarAmount(item.formatted_revenue) : '-'}
      </div>
      {!item.reviewed && (
        <Tooltip
          title={t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.reviewedBy", { reviewedBy: item.reviewed_by_user?.full_name || "-" })}
          arrow
          classes={{ tooltip: "tooltip" }}
        >
          <i className="ri-spam-line text-warning ri-20px" />
        </Tooltip>
      )}
      {isVerified && (
        <i className="ri-verified-badge-fill text-success" />
      )}
    </div>
  );

  const lastSyncNode = (
    <Tooltip
      title={`${t('invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.lastSync')} ${item?.last_sync_with_hospitable ?? '-'}`}
      arrow
      classes={{
        tooltip: 'tooltip',
      }}
    >
      <i className="ri-timer-line ri-20px"></i>
    </Tooltip>
  )

  return (
    <tr className={clsx(className, item?.reservation_status === RESERVATION_STATUS.reconciled && 'raw-danger')}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={item.id} width={headCell.width}>
              <div className="form-floating form-floating-outline">
                <Checkbox
                  variant='primary'
                  className='d-checkbox mb-0'
                  checked={formData.selectedReservations.includes(item?.id)}
                  disabled={view}
                  name={VALIDATION_FIELD_MAPPING.actions}
                  onChange={(_: any, name: string) => onInputChange(_, name, 'reservations', item?.id)}
                />
              </div>
            </td>
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.checkInDetails) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={checkInDetailsNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.guest) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={guestNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.reservationStatus) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              className={clsx((item?.reservation_status === RESERVATION_STATUS.cancelled || item?.reservation_status === RESERVATION_STATUS.expired) && "text-danger")}
              text={item?.reservation_status_name ?? '-'}
            />
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.hostPayout) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={hostPayoutNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.source) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.source_name ?? '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <Badge
                  text={item.status_name || "-"}
                  variant={getStatusVariant(item.status)}
                />
              }
            />
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.lastSync) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={lastSyncNode}
            />
          );
        }

        return (<ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] ?? "-"} />);
      })}
    </tr>
  );
};

export default Row;
